import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { User as UserIcon, Menu, LogOut } from 'lucide-react';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import LanguageSwitcher from '../LanguageSwitcher';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { t } = useTranslation('navigation');
  const { seller, user, logout } = useAuth();

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
      <div className="px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3 md:space-x-4 min-w-0">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
            aria-label="Toggle menu"
          >
            <Menu size={24} />
          </button>
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 truncate">
            {seller?.name || 'Seller Portal'}
          </h2>
        </div>

        <div className="flex items-center space-x-2 md:space-x-3 shrink-0">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>

          <NotificationDropdown />

          <Link
            to="/profile"
            className="flex items-center space-x-2 md:space-x-3 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title={t('menu.profile')}
          >
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate max-w-[140px]">{user?.email}</p>
            </div>
            {user?.profileImageUrl ? (
              <img
                src={user.profileImageUrl}
                alt={`${user.firstName} ${user.lastName}`}
                className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-8 h-8 md:w-10 md:h-10 bg-custom-primary rounded-full flex items-center justify-center">
                <UserIcon size={16} className="md:w-5 md:h-5 text-white" />
              </div>
            )}
          </Link>

          <button
            type="button"
            onClick={() => logout()}
            className="flex items-center gap-1.5 px-2 md:px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-red-600 transition-colors"
            title={t('menu.logout')}
            aria-label={t('menu.logout')}
          >
            <LogOut size={18} className="md:w-5 md:h-5" />
            <span className="hidden md:inline text-sm font-medium">{t('menu.logout')}</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
